import { __awaiter, __generator, __assign, __rest, __spreadArray } from 'tslib';
import { useCallback, useMemo, useEffect, useState } from 'react';
import axios from 'axios';
import { useMountedRef } from '@clover-util/use-mounted-ref';
import { useResolvedState } from '@clover-util/use-resolved-state';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function createAxiosDataRequestHook(useRequest) {
    /**
     * Returns a flag, async request function and synchronous cancel function
     *
     * Hook will cancel pending requests when unmounted.
     *
     * @example
     * const [isLoading, getMenu, cancelGetMenu] = useRequest((page: number) => {
     *   // return falsy to prevent the request
     *   if (page == 0) return;
     *
     *   return {
     *     url: `${merchantId}/menu`,
     *     params: { page }
     *   };
     * }, [merchantId]);
     *
     * useEffect(() => {
     *   if (currentPage >= 0) getMenu<MenuDataTransferable>(currentPage).then(...);
     * }, [currentPage]);
     *
     * @param factory {ConfigFactory} - function that can return falsy or an AxiosRequestConfig object; returning falsy prevents the request from going out
     * @param deps {DependencyList} - react dependency list
     * @returns RequestResult
     */
    return function useDataRequest(factory, deps) {
        var _this = this;
        if (deps === void 0) { deps = []; }
        var _a = useRequest(factory, deps), hasPending = _a[0], requestImpl = _a[1], cancel = _a[2];
        var request = useCallback(function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return __awaiter(_this, void 0, void 0, function () {
                var response;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4 /*yield*/, requestImpl.apply(void 0, args)];
                        case 1:
                            response = _a.sent();
                            return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                    }
                });
            });
        }, [requestImpl]);
        return Object.assign([hasPending, request, cancel], { hasPending: hasPending, request: request, cancel: cancel });
    };
}

/**
 * "High order hook" (HOH) - A hook returning function
 *
 * Allows for the composition of a useElementsResource hook based on a useResource hook
 *
 * A "Elements Resource" is defined as an array that is returned by a API call.
 *
 * COS follows a pattern of returning arrays wrapped in an "Elements" object.
 *
 * Typically rendering a component with data from an API call
 * we must retrieve a resource when the component mounts.
 *
 * This HOH provides a standardized wrapper for such behavior.
 *
 * Examples:
 * - useCosElementsResource - hook which uses a useCosResource ResourceHook
 * - usePerkaElementsResource - hook which uses a usePerkaResource ResourceHook
 *
 * To use this HOH, first define a custom ResourceHook, then declare your custom hook:
 *
 * const useCustomElementsResource = requestHook(useCustomResource);
 *
 * @param useHook {ResourceHook} hook which performs the request.
 */
function createAxiosArrayResourceHook(useHook) {
    return function useAxiosArrayResource(factory, deps) {
        var _a = useHook(factory, deps), hasPending = _a[0], response = _a[1], error = _a[2];
        var value = useMemo(function () { return response !== null && response !== void 0 ? response : []; }, [response]);
        return Object.assign([hasPending, value, error], { hasPending: hasPending, value: value, error: error });
    };
}

/**
 * "High order hook" (HOH) - A hook returning function
 *
 * Allows for the composition of a useElementsResource hook based on a useResource hook
 *
 * A "Elements Resource" is defined as an array that is returned by a API call.
 *
 * COS follows a pattern of returning arrays wrapped in an "Elements" object.
 *
 * Typically rendering a component with data from an API call
 * we must retrieve a resource when the component mounts.
 *
 * This HOH provides a standardized wrapper for such behavior.
 *
 * Examples:
 * - useCosElementsResource - hook which uses a useCosResource ResourceHook
 * - usePerkaElementsResource - hook which uses a usePerkaResource ResourceHook
 *
 * To use this HOH, first define a custom ResourceHook, then declare your custom hook:
 *
 * const useCustomElementsResource = requestHook(useCustomResource);
 *
 * @param useHook {ResourceHook} hook which performs the request.
 */
function createAxiosElementsResourceHook(useHook) {
    return function useAxiosElementsResource(factory, deps) {
        var _a = useHook(factory, deps), hasPending = _a[0], response = _a[1], error = _a[2];
        var value = useMemo(function () { var _a; return (_a = response === null || response === void 0 ? void 0 : response.elements) !== null && _a !== void 0 ? _a : []; }, [response]);
        return Object.assign([hasPending, value, error], { hasPending: hasPending, value: value, error: error });
    };
}

function createAxiosRequestHook(useInstance) {
    /**
     * Returns a flag, async request function and synchronous cancel function
     *
     * Hook will cancel pending requests when unmounted.
     *
     * @example
     * const [isLoading, getMenu, cancelGetMenu] = useAxiosRequest((page: number) => {
     *   // return falsy to prevent the request
     *   if (page == 0) return;
     *
     *   return {
     *     url: `${merchantId}/menu`,
     *     params: { page }
     *   };
     * }, [merchantId]);
     *
     * useEffect(() => {
     *   if (currentPage >= 0) getMenu<MenuDataTransferable>(currentPage).then(...);
     * }, [currentPage]);
     *
     * @param factory {ConfigFactory} - function that can return falsy or an AxiosRequestConfig object; returning falsy prevents the request from going out
     * @param deps {DependencyList} - react dependency list
     * @returns RequestResult
     */
    return function useAxiosRequest(factory, deps) {
        var _this = this;
        if (deps === void 0) { deps = []; }
        var mounted = useMountedRef();
        var instance = useInstance();
        var sources = useMemo(function () { return new Set(); }, []);
        var cancel = useCallback(function (message) { return sources.forEach(function (source) { return source.cancel(message); }); }, [sources]);
        useEffect(function () { return cancel; }, [cancel]);
        var _a = useState(false), hasPending = _a[0], setHasPending = _a[1];
        var request = useCallback(function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return __awaiter(_this, void 0, void 0, function () {
                var config, source, _a, method, rest, e_1;
                return __generator(this, function (_b) {
                    switch (_b.label) {
                        case 0:
                            config = factory.apply(void 0, args);
                            if (!config)
                                return [2 /*return*/];
                            source = axios.CancelToken.source();
                            sources.add(source);
                            setHasPending(true);
                            _a = config.method, method = _a === void 0 ? 'GET' : _a, rest = __rest(config, ["method"]);
                            _b.label = 1;
                        case 1:
                            _b.trys.push([1, 3, 4, 5]);
                            return [4 /*yield*/, instance.request(__assign(__assign({ method: method }, rest), { cancelToken: source.token }))];
                        case 2: return [2 /*return*/, _b.sent()];
                        case 3:
                            e_1 = _b.sent();
                            if (axios.isCancel(e_1)) {
                                window.console.warn("".concat(method, " ").concat(config.url, " has been canceled"), e_1.message);
                                return [2 /*return*/];
                            }
                            throw e_1;
                        case 4:
                            sources.delete(source);
                            if (mounted.current)
                                setHasPending(!!sources.size);
                            return [7 /*endfinally*/];
                        case 5: return [2 /*return*/];
                    }
                });
            });
        }, __spreadArray([instance, sources], deps, true));
        return Object.assign([hasPending, request, cancel], { hasPending: hasPending, request: request, cancel: cancel });
    };
}

/**
 * "High order hook" (HOH) - A hook returning function
 *
 * Allows for the composition of a useResource hook based on a useRequest hook
 *
 * A "Resource" is defined as data that is returned by a API call.
 *
 * Typically rendering a component with data from an API call
 * we must retrieve a resource when the component mounts.
 *
 * This HOH provides a standardized wrapper for such behavior.
 *
 * To use this HOH, first define a custom RequestHook, then declare your custom hook:
 *
 * const useCustomResource = requestHook(useCustomRequest);
 *
 * @param useHook {RequestHook} hook which performs the request.
 */
function createAxiosResourceHook(useHook) {
    /**
     * Returns a flag, value, and error for the resource described by the factory
     *
     * Hook will cancel pending requests and re-fetch when dependencies change.
     *
     * Note: if request returns undefined, the old value will not be cleared.
     *
     * @example
     * const [ hasPending, list, error ] = useRequest<List>(() => {
     *   // return falsy to prevent the request
     *   if (page == 0) return;
     *
     *   return {
     *     url: `${id}/items`,
     *     params: { page }
     *   };
     * }, [page]);
     *
     * @param factory {ConfigFactory} - function that can return falsy or an AxiosRequestConfig object; returning falsy prevents the request from going out
     * @param deps {DependencyList} - react dependency list
     */
    return function useAxiosResource(factory, deps) {
        var _a = useHook(factory, deps), hasPending = _a[0], response = _a[1], error = _a[2];
        var value = useMemo(function () { return response === null || response === void 0 ? void 0 : response.data; }, [response]);
        return Object.assign([hasPending, value, error], { hasPending: hasPending, value: value, error: error });
    };
}

/**
 * "High order hook" (HOH) - A hook returning function
 *
 * Allows for the composition of a useResource hook based on a useRequest hook
 *
 * Typically rendering a component with data from an API call
 * we must retrieve a resource when the component mounts.
 *
 * This HOH provides a standardized wrapper for such behavior.
 *
 * To use this HOH, first define a custom RequestHook, then declare your custom hook:
 *
 * const useCustomResponse = createAxiosResponseHook(useCustomRequest);
 *
 * @param useHook {RequestHook} hook which performs the request.
 */
function createAxiosResponseHook(useHook) {
    /**
     * Returns a flag, value, and error for the resource described by the factory
     *
     * Hook will cancel pending requests and re-fetch when dependencies change.
     *
     * Note: if request returns undefined, the old value will not be cleared.
     *
     * @example
     * const [ hasPending, list, error ] = useAxiosResponse<List>(() => {
     *   // return falsy to prevent the request
     *   if (page == 0) return;
     *
     *   return {
     *     url: `${id}/items`,
     *     params: { page }
     *   };
     * }, [page]);
     *
     * @param factory {ConfigFactory} - function that can return falsy or an AxiosRequestConfig object; returning falsy prevents the request from going out
     * @param deps {DependencyList} - react dependency list
     */
    return function useAxiosResponse(factory, deps) {
        var _a = useHook(factory, deps), hasPending = _a[0], request = _a[1], cancel = _a[2];
        var _b = useResolvedState(request, cancel), response = _b[0], error = _b[1];
        return Object.assign([hasPending, response, error], { hasPending: hasPending, response: response, error: error });
    };
}

function createAxiosHooks(name, useInstance) {
    var _a;
    var useAxiosRequest = createAxiosRequestHook(useInstance);
    var useAxiosResponse = createAxiosResponseHook(useAxiosRequest);
    var useAxiosResource = createAxiosResourceHook(useAxiosResponse);
    var useAxiosElementsResource = createAxiosElementsResourceHook(useAxiosResource);
    var useAxiosArrayResource = createAxiosArrayResourceHook(useAxiosResource);
    var useAxiosDataRequest = createAxiosDataRequestHook(useAxiosRequest);
    return Object.assign([
        useAxiosRequest,
        useAxiosResponse,
        useAxiosResource,
        useAxiosElementsResource,
        useAxiosArrayResource,
        useAxiosDataRequest,
    ], (_a = {},
        _a["use".concat(name, "Request")] = useAxiosRequest,
        _a["use".concat(name, "Response")] = useAxiosResponse,
        _a["use".concat(name, "Resource")] = useAxiosResource,
        _a["use".concat(name, "ElementsResource")] = useAxiosElementsResource,
        _a["use".concat(name, "ArrayResource")] = useAxiosArrayResource,
        _a["use".concat(name, "DataRequest")] = useAxiosDataRequest,
        _a));
}

function useStaticAxiosInstance() {
    return axios;
}

var _a;
var useStaticAxiosRequest = (_a = createAxiosHooks('StaticAxios', useStaticAxiosInstance), _a.useStaticAxiosRequest), useStaticAxiosResponse = _a.useStaticAxiosResponse, useStaticAxiosResource = _a.useStaticAxiosResource, useStaticAxiosElementsResource = _a.useStaticAxiosElementsResource, useStaticAxiosArrayResource = _a.useStaticAxiosArrayResource, useStaticAxiosDataRequest = _a.useStaticAxiosDataRequest;

export { createAxiosArrayResourceHook, createAxiosDataRequestHook, createAxiosElementsResourceHook, createAxiosHooks, createAxiosRequestHook, createAxiosResourceHook, createAxiosResponseHook, useStaticAxiosArrayResource, useStaticAxiosDataRequest, useStaticAxiosElementsResource, useStaticAxiosInstance, useStaticAxiosRequest, useStaticAxiosResource, useStaticAxiosResponse };
